import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { IconButton } from "@carbon/react";
import { Chat } from "@carbon/icons-react";
import { useUser } from "../../context";
import { isAssistantHidden, screenContextFromPath } from "../../utils";
import ScreenAssistantPanel from "./ScreenAssistantPanel";
import { useNarrowScreen } from "./useNarrowScreen";
import "./screenAssistant.scss";

/** Set on `<body>` while the side panel is open, so the page makes room for it. */
const BODY_CLASS = "screen-assistant-open";

/**
 * The assistant button on every signed-in screen, and the chat it opens: a side panel next to
 * the page on a desktop, a bottom sheet on a phone. It knows which record is on screen from the
 * route, and nothing is loaded until it is opened.
 */
const ScreenAssistant = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { isSeller, user } = useUser();
  const sheet = useNarrowScreen();
  const [open, setOpen] = useState(false);
  const [contextActive, setContextActive] = useState(true);

  const context = screenContextFromPath(
    pathname,
    isSeller ? "seller" : "buyer"
  );

  // A new screen is a new context: switching it off applied to the screen it was switched off on.
  useEffect(() => {
    setContextActive(true);
  }, [pathname]);

  useEffect(() => {
    const makeRoom = open && !sheet;
    document.body.classList.toggle(BODY_CLASS, makeRoom);
    return () => document.body.classList.remove(BODY_CLASS);
  }, [open, sheet]);

  if (isAssistantHidden(pathname)) return null;

  return (
    <>
      {open && sheet && (
        <div
          className="screen-assistant__backdrop"
          aria-hidden="true"
          onClick={() => setOpen(false)}
        />
      )}
      {open ? (
        <ScreenAssistantPanel
          sheet={sheet}
          companyId={user?.company?.id}
          context={context}
          contextActive={contextActive}
          onContextToggle={setContextActive}
          onClose={() => setOpen(false)}
        />
      ) : (
        <IconButton
          kind="primary"
          size="lg"
          align="left"
          label={t("screenAssistant.open")}
          className="screen-assistant__launcher"
          onClick={() => setOpen(true)}
        >
          <Chat size={20} />
        </IconButton>
      )}
    </>
  );
};

export default ScreenAssistant;
